import { isLevel, type Level } from '@arrow/puzzle-schema';

import { SITE_URL } from '../env.ts';
import { type ShareOutcome as ShareMethod, type ShareVariant, track } from '../platform/analytics.ts';

/**
 * Sharing: the deep links (a daily, a practice puzzle, a challenge), the share sheet
 * (with `share_pressed` tracking), and reading a shared link on arrival. The share TEXT
 * is the engine's (`buildChallengeShareText` / `buildResultShareText`), composed in
 * `share-flow.ts`.
 */

/**
 * Where a shared result points: the public site (`SITE_URL`, env.ts, from `VITE_SITE_URL`).
 * The deployed site, not `location.origin`, so a result shared from localhost or a
 * preview still links somewhere a friend can play.
 */
export const SHARE_URL = SITE_URL;

/**
 * The query params a share link may carry, all stripped from the address bar on
 * arrival (`readShareParams`):
 * - `s=1`: opened from a share;
 * - `l`: the level (`easy` | `medium` | `hard`);
 * - `r`: reserved for an encoded result (a challenge link), not written yet.
 * New share params go here so they're read and stripped the same way.
 */
export const SHARE_PARAMS = ['s', 'l', 'r'] as const;
export type ShareParam = (typeof SHARE_PARAMS)[number];
/** Share params beyond `s` and `l`, carried through as raw strings. */
export type ShareExtra = Partial<Record<Exclude<ShareParam, 's' | 'l'>, string>>;

export interface ShareTarget {
	/** The daily's number; null/0 for practice. */
	number?: number | null;
	level?: Level | undefined;
	/** Practice: links `/play/<id>`. */
	puzzleId?: string | undefined;
	/** Extra share params, e.g. `{ r: '<encoded result>' }` later. */
	extra?: ShareExtra;
}

/**
 * The deep link for a result: `/daily/42?s=1&l=hard` for a daily, `/play/<id>?s=1&l=…`
 * for practice, `/?s=1` when there's nothing more specific.
 */
export function shareUrl(target: ShareTarget, base: string = SHARE_URL): string {
	const number = target.number ?? 0;
	const path =
		number > 0 ? `/daily/${Math.floor(number)}` : target.puzzleId ? `/play/${encodeURIComponent(target.puzzleId)}` : '/';
	const params = new URLSearchParams({ s: '1' });
	if (target.level) params.set('l', target.level);
	for (const [key, value] of Object.entries(target.extra ?? {})) if (value) params.set(key, value);
	return `${base}${path}?${params.toString()}`;
}

export type { ShareMethod };

/** What's being shared, for the `share_pressed` event. */
export interface ShareMeta {
	number?: number | null;
	level?: Level | undefined;
	kind?: 'daily' | 'practice';
	variant?: ShareVariant;
	shareId?: string;
}

/** The challenge link: `/c/K7Q2` on the deployed site. */
export function challengeUrl(code: string, base: string = SHARE_URL): string {
	return `${base}/c/${encodeURIComponent(code)}`;
}

/** A short random id for one share press (`share_pressed.shareId`). */
export function newShareId(): string {
	try {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
	} catch {
		// Fall through.
	}
	return Math.random().toString(36).slice(2, 14).padEnd(12, '0');
}

/**
 * Shares `text`: the Web Share sheet where there is one (phones), else the clipboard.
 * Returns how it went and tracks `share_pressed` with that method. Never throws.
 * `cancelled` = the player dismissed the share sheet (nothing was copied).
 */
export async function shareResult(text: string, meta: ShareMeta = {}): Promise<ShareMethod> {
	const method = await deliver(text);
	track('share_pressed', {
		number: meta.number ?? null,
		...(meta.level ? { level: meta.level } : {}),
		...(meta.kind ? { kind: meta.kind } : {}),
		...(meta.variant ? { variant: meta.variant } : {}),
		...(meta.shareId ? { shareId: meta.shareId } : {}),
		method,
	});
	return method;
}

/** The share sheet's outcome in the pre-platform vocabulary. */
export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/**
 * @deprecated Use `shareResult(text, meta)` (it tracks `share_pressed`). Kept for the
 * current ResultSheet: same behaviour, answering `shared` / `copied`, and untracked.
 */
export async function shareText(text: string): Promise<ShareOutcome> {
	const method = await deliver(text);
	return method === 'native' ? 'shared' : method === 'clipboard' ? 'copied' : method;
}

/** The share sheet with `text` (phones), else the clipboard; untracked. The nest invite uses it. */
export function shareOrCopy(text: string): Promise<ShareMethod> {
	return deliver(text);
}

/** Copies `text`, no share sheet. Never throws. */
export async function copyText(text: string): Promise<boolean> {
	try {
		if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// Fall through to the legacy path.
	}
	let area: HTMLTextAreaElement | undefined;
	try {
		area = document.createElement('textarea');
		area.value = text;
		area.setAttribute('readonly', '');
		area.style.position = 'fixed';
		area.style.opacity = '0';
		document.body.appendChild(area);
		area.select();
		return document.execCommand('copy');
	} catch {
		return false;
	} finally {
		area?.remove();
	}
}

async function deliver(text: string): Promise<ShareMethod> {
	const nav = typeof navigator === 'undefined' ? undefined : navigator;
	if (nav?.share) {
		try {
			await nav.share({ text });
			return 'native';
		} catch (err) {
			if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
			// Fall through to the clipboard.
		}
	}
	try {
		if (nav?.clipboard?.writeText) {
			await nav.clipboard.writeText(text);
			return 'clipboard';
		}
	} catch {
		// Fall through to the legacy path.
	}
	let area: HTMLTextAreaElement | undefined;
	try {
		area = document.createElement('textarea');
		area.value = text;
		area.setAttribute('readonly', '');
		area.style.position = 'fixed';
		area.style.opacity = '0';
		document.body.appendChild(area);
		area.select();
		return document.execCommand('copy') ? 'clipboard' : 'failed';
	} catch {
		return 'failed';
	} finally {
		area?.remove();
	}
}

/** What a shared link carried. */
export interface ShareParams {
	/** The link came from a share (`s=1`). */
	fromShare: boolean;
	/** The level it pointed at (`l`), if valid. */
	level?: Level;
	/** Any other share params present (`r` …), raw, for later use. */
	extra: ShareExtra;
}

/**
 * Reads the share params from `url` (default: the current page) and strips them from
 * the address bar with `history.replaceState` (keeping the path, other params and the
 * hash), so a reload or a copied address isn't another share open. Call once on boot;
 * the caller tracks `shared_link_opened` when `fromShare`. Never throws.
 */
export function readShareParams(url?: string): ShareParams {
	const out: ShareParams = { fromShare: false, extra: {} };
	try {
		const href = url ?? (typeof window === 'undefined' ? undefined : window.location.href);
		if (!href) return out;
		const parsed = new URL(href);
		const params = parsed.searchParams;
		out.fromShare = params.get('s') === '1';
		const level = params.get('l');
		if (isLevel(level)) out.level = level;
		const result = params.get('r');
		if (result) out.extra.r = result;

		const present = SHARE_PARAMS.filter((key) => params.has(key));
		if (present.length > 0 && typeof window !== 'undefined' && window.history?.replaceState) {
			const here = new URL(window.location.href);
			// Only rewrite the address bar when it's the url that was read.
			if (url === undefined || here.href === parsed.href) {
				for (const key of present) here.searchParams.delete(key);
				const search = here.searchParams.toString();
				window.history.replaceState(window.history.state, '', `${here.pathname}${search ? `?${search}` : ''}${here.hash}`);
			}
		}
	} catch {
		// A url we can't read carries nothing.
	}
	return out;
}
